import Duck from 'extensible-duck';
import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { normalize, schema } from 'normalizr';
import moment from 'moment';

import requestsManager from 'lib/requestsManagerAxios'; // eslint-disable-line
import { blue } from '@material-ui/core/colors';

const todoSchema = new schema.Entity('todos');

export default new Duck({
  namespace: 'organization-config',
  store: 'dashboard',
  types: [
    'INITIALIZE',
    'INITIALIZE_PENDING',
    'INITIALIZE_FULFILLED',
    'INITIALIZE_REJECTED',
    'DISMISS_TODO',
    'DISMISS_TODO_PENDING',
    'DISMISS_TODO_FULFILLED',
    'DISMISS_TODO_REJECTED',
    'FETCH_ANALYTICS',
    'FETCH_ANALYTICS_PENDING',
    'FETCH_ANALYTICS_FULFILLED',
    'FETCH_ANALYTICS_REJECTED',
    'FETCH_TIME_FRAME_STATS',
    'FETCH_TIME_FRAME_STATS_PENDING',
    'FETCH_TIME_FRAME_STATS_FULFILLED',
    'FETCH_TIME_FRAME_STATS_REJECTED',
    'SET_TIMEFRAME',
    'TELL_US_MORE',
    'TELL_US_MORE_PENDING',
    'TELL_US_MORE_FULFILLED',
    'TELL_US_MORE_REJECTED',
    'UPDATE_PHONE',
    'UPDATE_PHONE_PENDING',
    'UPDATE_PHONE_FULFILLED',
    'UPDATE_PHONE_REJECTED',
    'UPLOAD_LOGO',
    'UPLOAD_LOGO_PENDING',
    'UPLOAD_LOGO_FULFILLED',
    'UPLOAD_LOGO_REJECTED',
  ],
  initialState: Immutable.Map({
    // devToolsOn: true,
    activePrograms: -1,
    currentPeriodEnd: 0,
    fetchAnalyticsPending: true,
    fetchTimeFrameStatsPending: true,
    initializePending: true,
    moneyCollected: -1,
    monthlyPeriodEnd: 0,
    registrantsRegistered: -1,
    remainingTexts: -1,
    remainingEmails: -1,
    setIsFetching: false,
    setIsSaving: false,
    showPhoneInput: false,
    timeFrame: 'month',
    totalPageviews: 0,
    $$analytics: Immutable.List([]),
    $$organizerStats: Immutable.List([]),
    $$entities: Immutable.Map({
      $$todos: Immutable.Map({}),
    }),
    $$todoIds: Immutable.OrderedSet([]),
  }),
  reducer: ($$state, { type, payload, _meta }, { types }) => {
    switch (type) {
      case types.DISMISS_TODO_FULFILLED:
        return $$state;
      case types.FETCH_TIME_FRAME_STATS_PENDING:
        return $$state.merge({ fetchTimeFrameStatsPending: true });
      case types.FETCH_ANALYTICS_PENDING:
        return $$state.merge({ fetchAnalyticsPending: true });
      case types.UPDATE_PHONE_PENDING:
      case types.DISMISS_TODO_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_PHONE_REJECTED:
      case types.DISMISS_TODO_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.UPDATE_PHONE_FULFILLED:
        return $$state.merge({ setIsSaving: false });
      case types.INITIALIZE_FULFILLED: {
        const {
          remainingTexts,
          remainingEmails,
          activePrograms,
          currentPeriodEnd,
          monthlyPeriodEnd,
          todos,
          showPhoneInput,
        } = payload.data;
        const normalizedTodos = normalize(todos, [todoSchema]);
        return $$state
          .mergeDeep({
            remainingTexts,
            remainingEmails,
            activePrograms,
            currentPeriodEnd,
            monthlyPeriodEnd,
            showPhoneInput,
            $$entities: {
              $$todos: Immutable.fromJS(normalizedTodos.entities.todos),
            },
            setIsSaving: false,
          })
          .set('$$todoIds', Immutable.OrderedSet(normalizedTodos.result));
      }
      case types.FETCH_ANALYTICS_FULFILLED:
        return $$state
          .merge({
            fetchAnalyticsPending: false,
            totalPageviews: parseInt(payload.data.total, 10),
          })
          .set('$$analytics', Immutable.fromJS(payload.data.rows));
      case types.FETCH_TIME_FRAME_STATS_FULFILLED: {
        const {
          moneyCollected,
          registrantsRegistered,
          organizerStats,
        } = payload.data;
        return $$state
          .merge({
            moneyCollected,
            registrantsRegistered,
            fetchTimeFrameStatsPending: false,
          })
          .set('$$organizerStats', Immutable.fromJS(organizerStats));
      }
      case types.SET_TIMEFRAME:
        return $$state.set('timeFrame', payload.timeFrame);
      case types.TELL_US_MORE_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.TELL_US_MORE_FULFILLED:
      case types.TELL_US_MORE_REJECTED:
        return $$state.merge({ setIsSaving: false });
      default:
        return $$state;
    }
  },
  selectors: {
    $$analytics: ($$state) => $$state.get('$$analytics'),
    chartData: new Duck.Selector((_selectors) =>
      createSelector(
        ($$state) => $$state.get('$$analytics'),
        ($$state) => $$state.get('totalPageviews'),
        ($$analytics, totalPageviews) => {
          const labels = $$analytics.map((el) => el.get(0)).toJS();
          const data = $$analytics.map((el) => el.get(1)).toJS();
          return {
            labels,
            datasets: [
              {
                label: `Website Views (Total: ${totalPageviews.toLocaleString()})`,
                backgroundColor: blue[100],
                borderColor: blue[500],
                data,
              },
            ],
          };
        }
      )
    ),
    $$todos: new Duck.Selector((_selectors) =>
      createSelector(
        ($$state) => $$state.getIn(['$$entities', '$$todos']),
        ($$state) => $$state.get('$$todoIds'),
        ($$todoEntities, $$todoIds) => {
          const $$todos = $$todoIds.map((todoId) => {
            const $$todo = $$todoEntities.get(todoId.toString());
            return $$todo.get('completedOn') &&
              moment().startOf('day') > moment($$todo.get('completedOn'))
              ? null
              : $$todo;
          });
          return $$todos.filterNot(($$todo) => $$todo === null);
        }
      )
    ),
  },
  creators: ({ types }) => ({
    initialize: () => ({
      type: types.INITIALIZE,
      payload: requestsManager.get('/admin/dashboard.json'),
    }),
    dismissTodo: (todo_id, owner) => ({
      type: types.DISMISS_TODO,
      payload: requestsManager.post('/admin/dismissed_todos', {
        todo_id,
        owner,
      }),
    }),
    fetchAnalytics: (timeFrame) => ({
      type: types.FETCH_ANALYTICS,
      payload: requestsManager.get(
        `/admin/api/ga/dashboard_graph_pageviews.json?timeframe=${timeFrame}`
      ),
    }),
    fetchTimeFrameStats: (timeFrame) => ({
      type: types.FETCH_TIME_FRAME_STATS,
      payload: requestsManager.get(
        `/admin/dashboard/timeframe_stats.json?timeframe=${timeFrame}`
      ),
    }),
    setTimeFrame: (timeFrame) => ({
      type: types.SET_TIMEFRAME,
      payload: { timeFrame },
    }),
    tellUsMore: (text) => ({
      type: types.TELL_US_MORE,
      payload: requestsManager.post('/admin/dismissed_todos/tell_us_more', {
        text,
      }),
    }),
    updatePhone: (phone) => ({
      type: types.UPDATE_PHONE,
      payload: requestsManager.patch('/admin/users', { user: { phone } }),
    }),
    uploadLogo: (form) => ({
      type: types.UPLOAD_LOGO,
      payload: requestsManager.patch('/admin/organization/update', form),
    }),
  }),
});
